/**
 * Mobile Top Bar — Frosted glass strip with floating icons
 *
 * Hamburger (left) | View name (center) | Toggle + Filter (right)
 * Settings/Profile/Insights are in the drawer, not here.
 *
 * Only visible below the `md` breakpoint (< 768px).
 */

import { Menu, MessageCircle, Funnel } from 'lucide-react';

interface MobileTopBarProps {
  currentViewName: string;
  isChatActive: boolean;
  onOpenDrawer: () => void;
  onToggleView: () => void;
  onFiltersClick?: () => void;
}

export function MobileTopBar({
  currentViewName,
  isChatActive,
  onOpenDrawer,
  onToggleView,
  onFiltersClick,
}: MobileTopBarProps) {
  return (
    <div
      className="md:hidden fixed inset-x-0 top-0 z-40 overflow-hidden"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Fallback tinted gradient — dark mode */}
      <div
        aria-hidden="true"
        data-testid="mobile-top-bar-fallback"
        className="pointer-events-none absolute inset-0 hidden dark:block"
        style={{
          background:
            'linear-gradient(to bottom, rgba(13,21,32,1) 0%, rgba(13,21,32,0.9) 38%, rgba(13,21,32,0.55) 68%, rgba(13,21,32,0) 100%)',
        }}
      />
      {/* Fallback tinted gradient — light mode (#FAFAFA = surface-base-light) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 block dark:hidden"
        style={{
          background:
            'linear-gradient(to bottom, rgba(250,250,250,1) 0%, rgba(250,250,250,0.9) 38%, rgba(250,250,250,0.55) 68%, rgba(250,250,250,0) 100%)',
        }}
      />

      {/* Blur layer with progressive mask */}
      <div
        aria-hidden="true"
        data-testid="mobile-top-bar-blur"
        className="pointer-events-none absolute inset-0 backdrop-blur-md"
        style={{
          maskImage:
            'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 42%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 42%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex items-center px-3 pt-2 pb-6">
        {/* Left: hamburger + view name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            onClick={onOpenDrawer}
            className="w-10 h-10 rounded-full bg-surface-raised-light/60 dark:bg-surface-raised-dark/60 flex shrink-0 items-center justify-center text-text-secondary-light dark:text-text-secondary-dark shadow-sm"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-mono text-sm font-semibold uppercase tracking-wider text-text-primary-light dark:text-text-primary-dark truncate">
            <span className="text-primary-500 mr-1">/</span>
            {isChatActive ? 'Ask Danny' : currentViewName}
          </span>
        </div>

        {/* Right: filter (tasks only) + toggle (always rightmost) */}
        <div className="flex shrink-0 items-center gap-1.5">
          {!isChatActive && onFiltersClick && (
            <button
              onClick={onFiltersClick}
              className="w-10 h-10 rounded-full bg-surface-raised-light/60 dark:bg-surface-raised-dark/60 flex items-center justify-center text-text-secondary-light dark:text-text-secondary-dark shadow-sm"
              aria-label="Filters"
            >
              <Funnel className="w-5 h-5" />
            </button>
          )}

          <button
            onClick={onToggleView}
            className={
              isChatActive
                ? 'w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-surface-base-dark shadow-sm'
                : 'w-10 h-10 rounded-full bg-surface-raised-light/60 dark:bg-surface-raised-dark/60 flex items-center justify-center text-text-secondary-light dark:text-text-secondary-dark shadow-sm'
            }
            aria-label={isChatActive ? 'Switch to tasks' : 'Switch to chat'}
          >
            <MessageCircle className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
